// SuperLogsPage — /super/logs.
//
// Two tabs:
//   1. Log Viewer    — paginated table of system_logs with full filter bar,
//                      row expand, auto-refresh, manual delete, and the
//                      notification_failures bridge section.
//   2. Retention     — 21-row inline editor for log_retention_settings.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, RefreshCw, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Tabs } from '../../../components/ui/Tabs/Tabs';
import { Modal } from '../../../components/ui/Modal/Modal';
import { useDebounce } from '../../../hooks/useDebounce';
import {
  CATEGORY_OPTIONS,
  MODULE_OPTIONS,
  SEVERITY_OPTIONS,
  SOURCE_OPTIONS,
  useDeleteLogs,
  useLogRetention,
  useSystemLogs,
  useUpdateRetention,
  type LogCategory,
  type LogEntry,
  type LogModule,
  type LogSeverity,
  type LogSource,
  type RetentionRow,
  type SystemLogsFilters,
} from '../../../hooks/useSystemLogs';
import {
  useMobileSubmissionLogs,
  type MobileSubmissionApp,
  type MobileSubmissionLogEntry,
  type MobileSubmissionOutcome,
  type ListMobileSubmissionLogsFilters,
} from '../hooks/useSuper';
import styles from './SuperLogsPage.module.css';

type ActiveTab = 'viewer' | 'submissions' | 'retention';

const TAB_ITEMS = [
  { id: 'viewer', label: 'Log Viewer' },
  { id: 'submissions', label: 'Mobile Submissions' },
  { id: 'retention', label: 'Retention Settings' },
];

const TAB_META: Record<ActiveTab, { title: string; subtitle: string }> = {
  viewer: {
    title: 'System logs',
    subtitle:
      'Cross-tenant log viewer. Refreshes every 15 s while this tab is active.',
  },
  submissions: {
    title: 'Mobile submissions',
    subtitle:
      'Cross-tenant audit of Dispatch receipt submissions and Skip Manager actions.',
  },
  retention: {
    title: 'Retention settings',
    subtitle: 'Per-module retention policy. Cleanup runs daily at 02:00.',
  },
};

const SUBMISSION_APP_OPTIONS: MobileSubmissionApp[] = ['dispatch', 'skip_manager'];
const SUBMISSION_OUTCOME_OPTIONS: MobileSubmissionOutcome[] = [
  'success',
  'duplicate_blocked',
  'conflict_resolved',
  'error',
];

function outcomeVariant(
  o: MobileSubmissionOutcome,
): 'success' | 'warning' | 'info' | 'danger' {
  if (o === 'success') return 'success';
  if (o === 'error') return 'danger';
  if (o === 'duplicate_blocked') return 'warning';
  return 'info';
}

// MariaDB returns the JSON `context` column as a string; tolerate both a raw
// string and an already-parsed object so the expanded row always pretty-prints.
function renderContext(context: unknown): string {
  if (context === null || context === undefined) return '(none)';
  if (typeof context === 'string') {
    try {
      return JSON.stringify(JSON.parse(context), null, 2);
    } catch {
      return context;
    }
  }
  return JSON.stringify(context, null, 2);
}

const PER_PAGE_OPTIONS = [25, 50, 100, 200] as const;
const DEFAULT_PER_PAGE = 50;
const MESSAGE_TRIM_AT = 80;
const SEARCH_DEBOUNCE_MS = 400;

function severityVariant(s: LogSeverity): 'danger' | 'warning' | 'info' {
  if (s === 'error') return 'danger';
  if (s === 'warning') return 'warning';
  return 'info';
}

function severityLabel(s: LogSeverity): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
}

function formatIsoFull(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString();
}

function trimMessage(text: string): string {
  if (text.length <= MESSAGE_TRIM_AT) return text;
  return `${text.slice(0, MESSAGE_TRIM_AT).trimEnd()}…`;
}

function describeDeleteScope(filters: {
  module?: LogModule;
  severity?: LogSeverity;
  beforeDate: string;
}): string {
  const parts: string[] = [];
  if (filters.severity !== undefined) {
    parts.push(`${filters.severity}-level`);
  }
  if (filters.module !== undefined) {
    parts.push(filters.module);
  }
  const scope = parts.length > 0 ? parts.join(' ') : 'ALL';
  if (filters.beforeDate.length === 0) {
    return parts.length > 0
      ? `You are about to delete all ${scope} logs.`
      : 'You are about to delete ALL logs matching no filters. This cannot be undone.';
  }
  return `You are about to delete all ${scope} logs older than ${filters.beforeDate}.`;
}

export function SuperLogsPage(): JSX.Element {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ActiveTab>('viewer');

  return (
    <div className={styles.page}>
      <PageHeader
        title={TAB_META[tab].title}
        subtitle={TAB_META[tab].subtitle}
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="ghost"
              icon={ArrowLeft}
              onClick={() => navigate('/super')}
            >
              Back
            </Button>
          </div>
        }
      />

      <Tabs
        className={styles.tabs}
        items={TAB_ITEMS}
        value={tab}
        onChange={(id) => setTab(id as ActiveTab)}
        ariaLabel="Super logs tabs"
      />

      {tab === 'viewer' ? (
        <LogViewerTab />
      ) : tab === 'submissions' ? (
        <MobileSubmissionsTab />
      ) : (
        <RetentionTab />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Log Viewer tab
// ---------------------------------------------------------------------------

function LogViewerTab(): JSX.Element {
  const [moduleFilter, setModuleFilter] = useState<LogModule | ''>('');
  const [severityFilter, setSeverityFilter] = useState<LogSeverity | ''>('');
  const [sourceFilter, setSourceFilter] = useState<LogSource | ''>('');
  const [companyId, setCompanyId] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<LogCategory | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [failuresOpen, setFailuresOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  const filters: SystemLogsFilters = useMemo(() => {
    const out: SystemLogsFilters = { page, per_page: perPage };
    if (moduleFilter !== '') out.module = moduleFilter;
    if (severityFilter !== '') out.severity = severityFilter;
    if (sourceFilter !== '') out.source = sourceFilter;
    if (categoryFilter !== '') out.category = categoryFilter;
    if (companyId.length > 0) out.company_id = companyId;
    if (debouncedSearch.length > 0) out.search = debouncedSearch;
    if (fromDate.length > 0) out.from = fromDate;
    if (toDate.length > 0) out.to = toDate;
    return out;
  }, [
    page,
    perPage,
    moduleFilter,
    severityFilter,
    sourceFilter,
    categoryFilter,
    companyId,
    debouncedSearch,
    fromDate,
    toDate,
  ]);

  const query = useSystemLogs(filters);
  const data = query.data;

  // Reset page when filters change (page itself is excluded from the trigger
  // so changing page doesn't re-bounce to 1).
  const resetPage = useRef(false);
  useEffect(() => {
    if (resetPage.current) {
      setPage(1);
      setExpandedId(null);
    }
    resetPage.current = true;
  }, [
    moduleFilter,
    severityFilter,
    sourceFilter,
    categoryFilter,
    companyId,
    debouncedSearch,
    fromDate,
    toDate,
    perPage,
  ]);

  // "Last updated" indicator. Updated when query.dataUpdatedAt changes.
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState('—');
  useEffect(() => {
    if (query.dataUpdatedAt === 0) return;
    function tick() {
      const sec = Math.max(0, Math.round((Date.now() - query.dataUpdatedAt) / 1000));
      setLastUpdatedLabel(`${sec}s ago`);
    }
    tick();
    const id = window.setInterval(tick, 5000);
    return () => window.clearInterval(id);
  }, [query.dataUpdatedAt]);

  function clearFilters() {
    setModuleFilter('');
    setSeverityFilter('');
    setSourceFilter('');
    setCompanyId('');
    setCategoryFilter('');
    setSearchInput('');
    setFromDate('');
    setToDate('');
  }

  const logs: LogEntry[] = data?.logs ?? [];
  const pagination = data?.pagination;
  const failures = data?.notification_failures ?? [];

  if (query.error) {
    return (
      <div className={styles.errorBanner} role="alert">
        Failed to load logs: {query.error.message}
      </div>
    );
  }

  return (
    <>
      <Card flush>
        <div className={styles.toolbar}>
          <Button
            variant="danger"
            icon={Trash2}
            onClick={() => setDeleteOpen(true)}
          >
            Delete Logs
          </Button>
          <div className={styles.toolbarMeta} aria-live="polite">
            <RefreshCw size={12} aria-hidden className={styles.refreshIcon} />
            Last updated: {lastUpdatedLabel}
          </div>
        </div>

        <div className={styles.filters}>
          <div className={styles.filterField}>
            <label htmlFor="logs-module">Module</label>
            <select
              id="logs-module"
              className={styles.filterInput}
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value as LogModule | '')}
            >
              <option value="">All</option>
              {MODULE_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div className={styles.filterField}>
            <label htmlFor="logs-severity">Severity</label>
            <select
              id="logs-severity"
              className={styles.filterInput}
              value={severityFilter}
              onChange={(e) =>
                setSeverityFilter(e.target.value as LogSeverity | '')
              }
            >
              <option value="">All</option>
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className={styles.filterField}>
            <label htmlFor="logs-source">Source</label>
            <select
              id="logs-source"
              className={styles.filterInput}
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as LogSource | '')}
            >
              <option value="">All</option>
              {SOURCE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className={styles.filterField}>
            <label htmlFor="logs-company">Company ID</label>
            <input
              id="logs-company"
              type="text"
              className={styles.filterInput}
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              placeholder="enc_id"
            />
          </div>
          <div className={styles.filterField}>
            <label htmlFor="logs-category">Category</label>
            <select
              id="logs-category"
              className={styles.filterInput}
              value={categoryFilter}
              onChange={(e) =>
                setCategoryFilter(e.target.value as LogCategory | '')
              }
            >
              <option value="">All</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className={styles.filterField}>
            <label htmlFor="logs-search">Search</label>
            <input
              id="logs-search"
              type="search"
              className={styles.filterInput}
              value={searchInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearchInput(e.target.value)
              }
              placeholder="Message or context"
            />
          </div>
          <div className={styles.filterField}>
            <label htmlFor="logs-from">From date</label>
            <input
              id="logs-from"
              type="date"
              className={styles.filterInput}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className={styles.filterField}>
            <label htmlFor="logs-to">To date</label>
            <input
              id="logs-to"
              type="date"
              className={styles.filterInput}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className={styles.filterActions}>
            <Button variant="secondary" onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Severity</th>
                <th>Module</th>
                <th>Category</th>
                <th>Source</th>
                <th>Company</th>
                <th>Message</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading ? (
                <tr>
                  <td colSpan={7}>
                    <div className={styles.empty}>
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className={styles.emptyState}>
                      No log entries match these filters.
                    </div>
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const isOpen = expandedId === log.id;
                  return (
                    <Fragment key={log.id}>
                      <tr
                        className={styles.row}
                        onClick={() => setExpandedId(isOpen ? null : log.id)}
                      >
                        <td>
                          <Badge variant={severityVariant(log.severity)}>
                            {severityLabel(log.severity)}
                          </Badge>
                        </td>
                        <td>{log.module}</td>
                        <td>{log.category}</td>
                        <td>{log.source}</td>
                        <td>
                          {log.company_id !== null ? (
                            log.company_id
                          ) : (
                            <span className={styles.muted}>Platform</span>
                          )}
                        </td>
                        <td className={styles.message}>{trimMessage(log.message)}</td>
                        <td
                          className={styles.timestamp}
                          title={formatIsoFull(log.created_at)}
                        >
                          {formatRelative(log.created_at)}
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className={styles.expandedRow}>
                          <td
                            colSpan={7}
                            data-testid={`log-row-expanded-${log.id}`}
                          >
                            <div className={styles.expandedDetails}>
                              <div>
                                <h4>Message</h4>
                                <pre>{log.message}</pre>
                              </div>
                              <div>
                                <h4>Context</h4>
                                <pre>
                                  {log.context === null || log.context === undefined
                                    ? '(none)'
                                    : JSON.stringify(log.context, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <h4>Details</h4>
                                <pre>
                                  {JSON.stringify(
                                    {
                                      id: log.id,
                                      source: log.source,
                                      category: log.category,
                                      module: log.module,
                                      severity: log.severity,
                                      company_id: log.company_id,
                                      company_name: log.company_name,
                                      created_at: log.created_at,
                                    },
                                    null,
                                    2,
                                  )}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination !== undefined ? (
          <div className={styles.paginationWrap}>
            <div className={styles.perPageRow}>
              <label htmlFor="logs-per-page">Per page</label>
              <select
                id="logs-per-page"
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                {PER_PAGE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span>
                {pagination.total === 0
                  ? 'Showing 0 entries'
                  : `Showing ${(pagination.page - 1) * pagination.per_page + 1}–${Math.min(
                      pagination.page * pagination.per_page,
                      pagination.total,
                    )} of ${pagination.total} entries`}
              </span>
            </div>
            <div className={styles.perPageRow}>
              <Button
                variant="secondary"
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                  setExpandedId(null);
                }}
                disabled={pagination.page <= 1}
                aria-label="Previous page"
              >
                Previous
              </Button>
              <span>
                Page {pagination.page} of {Math.max(1, pagination.total_pages)}
              </span>
              <Button
                variant="secondary"
                onClick={() => {
                  setPage((p) => p + 1);
                  setExpandedId(null);
                }}
                disabled={pagination.page >= pagination.total_pages}
                aria-label="Next page"
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      <section className={styles.failuresSection}>
        <button
          type="button"
          className={styles.failuresHeader}
          onClick={() => setFailuresOpen((v) => !v)}
          aria-expanded={failuresOpen}
        >
          <span>
            Notification Failures (message_queue) — {failures.length}
          </span>
          {failuresOpen ? (
            <ChevronDown size={16} aria-hidden />
          ) : (
            <ChevronRight size={16} aria-hidden />
          )}
        </button>
        {failuresOpen ? (
          <div className={styles.failuresBody}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Message</th>
                  <th>Attempts</th>
                  <th>Last attempt</th>
                  <th>Queue ID</th>
                </tr>
              </thead>
              <tbody>
                {failures.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className={styles.emptyState}>
                        No notification failures.
                      </div>
                    </td>
                  </tr>
                ) : (
                  failures.map((f) => (
                    <tr key={f.message_queue_uuid}>
                      <td>{f.company_id}</td>
                      <td>{f.message}</td>
                      <td>
                        {f.attempts}/{f.max_attempts}
                      </td>
                      <td
                        className={styles.timestamp}
                        title={formatIsoFull(f.created_at)}
                      >
                        {formatRelative(f.created_at)}
                      </td>
                      <td className={styles.timestamp}>{f.message_queue_uuid}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {deleteOpen ? (
        <DeleteLogsModal
          currentFilters={{
            module: moduleFilter === '' ? undefined : moduleFilter,
            severity: severityFilter === '' ? undefined : severityFilter,
          }}
          onClose={() => setDeleteOpen(false)}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Delete modal
// ---------------------------------------------------------------------------

interface DeleteLogsModalProps {
  currentFilters: {
    module?: LogModule;
    severity?: LogSeverity;
  };
  onClose: () => void;
}

function DeleteLogsModal({ currentFilters, onClose }: DeleteLogsModalProps): JSX.Element {
  const [beforeDate, setBeforeDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const del = useDeleteLogs();

  const scope = describeDeleteScope({
    ...(currentFilters.module !== undefined ? { module: currentFilters.module } : {}),
    ...(currentFilters.severity !== undefined ? { severity: currentFilters.severity } : {}),
    beforeDate,
  });

  const confirmDisabled = beforeDate.length === 0 || del.isPending;

  async function onConfirm() {
    setError(null);
    setSuccessMessage(null);
    try {
      const res = await del.mutateAsync({
        confirm: true,
        ...(currentFilters.module !== undefined ? { module: currentFilters.module } : {}),
        ...(currentFilters.severity !== undefined ? { severity: currentFilters.severity } : {}),
        before_date: beforeDate,
      });
      setSuccessMessage(
        `Deleted ${res.deleted} log entr${res.deleted === 1 ? 'y' : 'ies'}.`,
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to delete logs.';
      setError(message);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Delete logs"
      size="sm"
      preventClose={del.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={del.isPending}>
            {successMessage !== null ? 'Close' : 'Cancel'}
          </Button>
          {successMessage === null ? (
            <Button
              variant="danger"
              onClick={onConfirm}
              loading={del.isPending}
              disabled={confirmDisabled}
            >
              Delete
            </Button>
          ) : null}
        </>
      }
    >
      <div className={styles.deleteScope}>
        {successMessage !== null ? (
          <div className={styles.modalSuccess} role="status">
            {successMessage}
          </div>
        ) : null}
        {error !== null ? (
          <div className={styles.modalError} role="alert">{error}</div>
        ) : null}
        <div className={styles.deleteScopeSummary}>{scope}</div>
        <div className={styles.deleteField}>
          <label htmlFor="delete-before-date">
            Delete entries older than (required)
          </label>
          <input
            id="delete-before-date"
            type="date"
            className={styles.filterInput}
            value={beforeDate}
            onChange={(e) => setBeforeDate(e.target.value)}
            disabled={successMessage !== null}
          />
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Mobile Submissions tab
// ---------------------------------------------------------------------------

function MobileSubmissionsTab(): JSX.Element {
  const [appFilter, setAppFilter] = useState<MobileSubmissionApp | ''>('');
  const [outcomeFilter, setOutcomeFilter] = useState<MobileSubmissionOutcome | ''>('');
  const [companyId, setCompanyId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [actionInput, setActionInput] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const debouncedAction = useDebounce(actionInput, SEARCH_DEBOUNCE_MS);

  const filters: ListMobileSubmissionLogsFilters = useMemo(() => {
    const out: ListMobileSubmissionLogsFilters = { page, limit: perPage };
    if (appFilter !== '') out.app = appFilter;
    if (outcomeFilter !== '') out.outcome = outcomeFilter;
    if (companyId.length > 0) out.company_id = companyId;
    if (driverId.length > 0) out.driver_id = driverId;
    if (debouncedAction.length > 0) out.action = debouncedAction;
    if (fromDate.length > 0) out.date_from = fromDate;
    if (toDate.length > 0) out.date_to = toDate;
    return out;
  }, [
    page,
    perPage,
    appFilter,
    outcomeFilter,
    companyId,
    driverId,
    debouncedAction,
    fromDate,
    toDate,
  ]);

  const query = useMobileSubmissionLogs(filters);
  const data = query.data;

  const resetPage = useRef(false);
  useEffect(() => {
    if (resetPage.current) {
      setPage(1);
      setExpandedId(null);
    }
    resetPage.current = true;
  }, [
    appFilter,
    outcomeFilter,
    companyId,
    driverId,
    debouncedAction,
    fromDate,
    toDate,
    perPage,
  ]);

  function clearFilters() {
    setAppFilter('');
    setOutcomeFilter('');
    setCompanyId('');
    setDriverId('');
    setActionInput('');
    setFromDate('');
    setToDate('');
  }

  const logs: MobileSubmissionLogEntry[] = data?.logs ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? perPage;
  const currentPage = data?.page ?? page;
  const totalPages = total === 0 ? 1 : Math.ceil(total / limit);

  if (query.error) {
    return (
      <div className={styles.errorBanner} role="alert">
        Failed to load submissions: {query.error.message}
      </div>
    );
  }

  return (
    <Card flush>
      <div className={styles.filters}>
        <div className={styles.filterField}>
          <label htmlFor="msl-app">App</label>
          <select
            id="msl-app"
            className={styles.filterInput}
            value={appFilter}
            onChange={(e) => setAppFilter(e.target.value as MobileSubmissionApp | '')}
          >
            <option value="">All</option>
            {SUBMISSION_APP_OPTIONS.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div className={styles.filterField}>
          <label htmlFor="msl-outcome">Outcome</label>
          <select
            id="msl-outcome"
            className={styles.filterInput}
            value={outcomeFilter}
            onChange={(e) =>
              setOutcomeFilter(e.target.value as MobileSubmissionOutcome | '')
            }
          >
            <option value="">All</option>
            {SUBMISSION_OUTCOME_OPTIONS.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className={styles.filterField}>
          <label htmlFor="msl-company">Company ID</label>
          <input
            id="msl-company"
            type="text"
            className={styles.filterInput}
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            placeholder="numeric id"
          />
        </div>
        <div className={styles.filterField}>
          <label htmlFor="msl-driver">Driver ID</label>
          <input
            id="msl-driver"
            type="text"
            className={styles.filterInput}
            value={driverId}
            onChange={(e) => setDriverId(e.target.value)}
            placeholder="numeric id"
          />
        </div>
        <div className={styles.filterField}>
          <label htmlFor="msl-action">Action</label>
          <input
            id="msl-action"
            type="search"
            className={styles.filterInput}
            value={actionInput}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setActionInput(e.target.value)
            }
            placeholder="e.g. receipt.created"
          />
        </div>
        <div className={styles.filterField}>
          <label htmlFor="msl-from">From date</label>
          <input
            id="msl-from"
            type="date"
            className={styles.filterInput}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <label htmlFor="msl-to">To date</label>
          <input
            id="msl-to"
            type="date"
            className={styles.filterInput}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <div className={styles.filterActions}>
          <Button variant="secondary" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>App</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Company</th>
              <th>Outcome</th>
              <th>Booking ref</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading ? (
              <tr>
                <td colSpan={7}>
                  <div className={styles.empty}>
                    <Spinner />
                  </div>
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className={styles.emptyState}>
                    No mobile submissions match these filters.
                  </div>
                </td>
              </tr>
            ) : (
              logs.map((log) => {
                const isOpen = expandedId === log.id;
                return (
                  <Fragment key={log.id}>
                    <tr
                      className={styles.row}
                      onClick={() => setExpandedId(isOpen ? null : log.id)}
                    >
                      <td>{log.app}</td>
                      <td>{log.action}</td>
                      <td>
                        {log.actor_name ?? <span className={styles.muted}>—</span>}
                        <span className={styles.muted}> ({log.actor_type})</span>
                      </td>
                      <td>
                        {log.company_name ?? log.company_id}
                      </td>
                      <td>
                        <Badge variant={outcomeVariant(log.outcome)}>
                          {log.outcome}
                        </Badge>
                      </td>
                      <td className={styles.timestamp}>
                        {log.booking_ref ?? <span className={styles.muted}>—</span>}
                      </td>
                      <td
                        className={styles.timestamp}
                        title={formatIsoFull(log.created_at)}
                      >
                        {formatRelative(log.created_at)}
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr className={styles.expandedRow}>
                        <td
                          colSpan={7}
                          data-testid={`submission-row-expanded-${log.id}`}
                        >
                          <div className={styles.expandedDetails}>
                            <div>
                              <h4>Context</h4>
                              <pre>{renderContext(log.context)}</pre>
                            </div>
                            <div>
                              <h4>Details</h4>
                              <pre>
                                {JSON.stringify(
                                  {
                                    id: log.id,
                                    uuid: log.uuid,
                                    app: log.app,
                                    action: log.action,
                                    outcome: log.outcome,
                                    actor_type: log.actor_type,
                                    driver_id: log.driver_id,
                                    staff_id: log.staff_id,
                                    actor_name: log.actor_name,
                                    company_id: log.company_id,
                                    company_name: log.company_name,
                                    booking_ref: log.booking_ref,
                                    receipt_id: log.receipt_id,
                                    customer_id: log.customer_id,
                                    ip_address: log.ip_address,
                                    user_agent: log.user_agent,
                                    request_id: log.request_id,
                                    created_at: log.created_at,
                                  },
                                  null,
                                  2,
                                )}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.paginationWrap}>
        <div className={styles.perPageRow}>
          <label htmlFor="msl-per-page">Per page</label>
          <select
            id="msl-per-page"
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
          >
            {PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span>
            {total === 0
              ? 'Showing 0 entries'
              : `Showing ${(currentPage - 1) * limit + 1}–${Math.min(
                  currentPage * limit,
                  total,
                )} of ${total} entries`}
          </span>
        </div>
        <div className={styles.perPageRow}>
          <Button
            variant="secondary"
            onClick={() => {
              setPage((p) => Math.max(1, p - 1));
              setExpandedId(null);
            }}
            disabled={currentPage <= 1}
            aria-label="Previous page"
          >
            Previous
          </Button>
          <span>
            Page {currentPage} of {Math.max(1, totalPages)}
          </span>
          <Button
            variant="secondary"
            onClick={() => {
              setPage((p) => p + 1);
              setExpandedId(null);
            }}
            disabled={currentPage >= totalPages}
            aria-label="Next page"
          >
            Next
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Retention tab
// ---------------------------------------------------------------------------

function RetentionTab(): JSX.Element {
  const query = useLogRetention();

  return (
    <Card flush>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Module</th>
              <th>Severity</th>
              <th>Retain (days)</th>
              <th>Last updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading ? (
              <tr>
                <td colSpan={5}>
                  <div className={styles.empty}>
                    <Spinner />
                  </div>
                </td>
              </tr>
            ) : (query.data ?? []).length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className={styles.emptyState}>
                    No retention rows configured.
                  </div>
                </td>
              </tr>
            ) : (
              <RetentionRows rows={query.data ?? []} />
            )}
          </tbody>
        </table>
      </div>
      <div className={styles.retentionFootnote}>
        Retention is enforced daily at 2am. Changes take effect on the next
        cleanup run.
      </div>
    </Card>
  );
}

interface RetentionRowsProps {
  rows: RetentionRow[];
}

function RetentionRows({ rows }: RetentionRowsProps): JSX.Element {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<number>(0);
  const [feedback, setFeedback] = useState<{ key: string; ok: boolean; text: string } | null>(null);
  const update = useUpdateRetention();

  function rowKey(r: RetentionRow): string {
    return `${r.module}:${r.severity}`;
  }

  function startEdit(r: RetentionRow) {
    setEditing(rowKey(r));
    setDraft(r.retain_days);
    setFeedback(null);
  }

  async function save(r: RetentionRow) {
    if (!Number.isFinite(draft) || draft < 1 || draft > 365) {
      setFeedback({ key: rowKey(r), ok: false, text: 'Must be 1–365' });
      return;
    }
    try {
      await update.mutateAsync({
        module: r.module,
        severity: r.severity,
        retain_days: draft,
      });
      setFeedback({ key: rowKey(r), ok: true, text: 'Saved' });
      setEditing(null);
    } catch (e) {
      const text = e instanceof Error ? e.message : 'Save failed';
      setFeedback({ key: rowKey(r), ok: false, text });
    }
  }

  function cancel() {
    setEditing(null);
    setFeedback(null);
  }

  return (
    <>
      {rows.map((r) => {
        const key = rowKey(r);
        const isEditing = editing === key;
        const rowFeedback = feedback?.key === key ? feedback : null;
        return (
          <tr key={key}>
            <td>{r.module}</td>
            <td>
              <Badge variant={severityVariant(r.severity)}>
                {severityLabel(r.severity)}
              </Badge>
            </td>
            <td>
              {isEditing ? (
                <input
                  type="number"
                  min={1}
                  max={365}
                  className={styles.retentionInput}
                  value={draft}
                  onChange={(e) => setDraft(Number(e.target.value))}
                  aria-label={`Retain days for ${r.module} ${r.severity}`}
                />
              ) : (
                r.retain_days
              )}
            </td>
            <td className={styles.timestamp} title={formatIsoFull(r.updated_at)}>
              {formatRelative(r.updated_at)}
            </td>
            <td>
              <div className={styles.retentionActions}>
                {isEditing ? (
                  <>
                    <Button
                      variant="primary"
                      onClick={() => void save(r)}
                      loading={update.isPending}
                    >
                      Save
                    </Button>
                    <Button variant="secondary" onClick={cancel} disabled={update.isPending}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => startEdit(r)}
                    disabled={editing !== null}
                  >
                    Edit
                  </Button>
                )}
                {rowFeedback !== null ? (
                  <span
                    className={
                      rowFeedback.ok
                        ? styles.retentionInlineFeedback
                        : styles.retentionInlineError
                    }
                  >
                    {rowFeedback.text}
                  </span>
                ) : null}
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
}
